import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import { buildErrorHtml, isBlank, isEmail } from "../utils/validation";

export default function InstructorManagement() {
  const [instructors, setInstructors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogsPage, setAccessLogsPage] = useState(1);
  const [accessLogsTotalPages, setAccessLogsTotalPages] = useState(1);
  const [accessLogsInstructor, setAccessLogsInstructor] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "INSTRUCTOR",
  });

  const [editingId, setEditingId] = useState(null);

  const fetchData = useCallback(async (pageNumber = 1, role = "", q = "") => {
    const params = new URLSearchParams();
    params.set("page", String(pageNumber));
    params.set("limit", "10");

    if (role) {
      params.set("role", role);
    }

    const search = String(q || "").trim();
    if (search) {
      params.set("q", search);
    }

    const response = await api.get(`/instructors?${params.toString()}`);

    if (Array.isArray(response.data)) {
      setInstructors(response.data);
      setPage(1);
      setTotalPages(1);
      return;
    }

    setInstructors(response.data?.data || []);
    setPage(response.data?.page || 1);
    setTotalPages(response.data?.totalPages || 1);
  }, []);

  useEffect(() => {
    fetchData(1, "", "");
  }, [fetchData]);

  function getRoleLabel(role) {
    if (role === "INSTRUCTOR") return "Instrutor";
    if (role === "COORDINATION" || role === "COORDINATOR") return "Coordenacao";
    if (role === "SUPER_ADMIN") return "Super Admin";
    return role || "-";
  }

  function formatDateTime(value) {
    if (!value) return "Nunca acessou";
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  }

  function applyFilters() {
    const nextQuery = searchInput.trim();
    setQuery(nextQuery);
    fetchData(1, roleFilter, nextQuery);
  }

  function clearFilters() {
    setRoleFilter("");
    setSearchInput("");
    setQuery("");
    fetchData(1, "", "");
  }

  async function fetchAccessLogs(instructor, pageNumber = 1) {
    const params = new URLSearchParams();
    params.set("instructorId", String(instructor.id));
    params.set("page", String(pageNumber));
    params.set("limit", "10");

    const response = await api.get(`/instructors/access-logs?${params.toString()}`);
    setAccessLogsInstructor(instructor);
    setAccessLogs(response.data?.data || []);
    setAccessLogsPage(response.data?.page || 1);
    setAccessLogsTotalPages(response.data?.totalPages || 1);
  }

  function closeAccessLogs() {
    setAccessLogsInstructor(null);
    setAccessLogs([]);
    setAccessLogsPage(1);
    setAccessLogsTotalPages(1);
  }

  async function handleSubmit() {
    try {
      const errors = [];

      if (isBlank(form.name)) {
        errors.push("Nome e obrigatorio.");
      }

      if (!editingId && !isEmail(form.email)) {
        errors.push("Informe um email valido.");
      }

      if (!editingId && isBlank(form.password)) {
        errors.push("Senha e obrigatoria.");
      }

      if (!editingId && String(form.password || "").trim().length < 6) {
        errors.push("Senha deve ter ao menos 6 caracteres.");
      }

      if (isBlank(form.role)) {
        errors.push("Selecione uma funcao.");
      }

      if (errors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Campos invalidos",
          html: buildErrorHtml(errors),
        });
        return;
      }

      if (editingId) {
        await api.put(`/instructors/${editingId}`, {
          name: form.name,
          role: form.role,
        });

        Swal.fire("Atualizado com sucesso");
        setEditingId(null);
      } else {
        await api.post("/instructors", form);
        Swal.fire("Usuario criado com sucesso");
      }

      setForm({
        name: "",
        email: "",
        password: "",
        role: "INSTRUCTOR",
      });

      fetchData(page, roleFilter, query);
    } catch (err) {
      Swal.fire("Erro", err.response?.data?.error || "Erro inesperado", "error");
    }
  }

  async function handleDelete(id) {
    const confirm = await Swal.fire({
      title: "Excluir usuario?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!confirm.isConfirmed) return;

    await api.delete(`/instructors/${id}`);
    fetchData(page, roleFilter, query);
  }

  async function handleResetPassword(id) {
    const confirm = await Swal.fire({
      title: "Resetar senha?",
      text: "Senha sera redefinida para 123456",
      icon: "question",
      showCancelButton: true,
    });

    if (!confirm.isConfirmed) return;

    await api.put(`/instructors/${id}/reset-password`);
    Swal.fire("Senha resetada para 123456");
  }

  function handleEdit(instructor) {
    setEditingId(instructor.id);
    setForm({
      name: instructor.name,
      email: instructor.email,
      password: "",
      role: instructor.role,
    });
  }

  return (
    <Layout>
      <h3 className="mb-4">Gestao de Usuarios</h3>

      <div className="card shadow-sm p-4 mb-4">
        <div className="row">
          <div className="col-md-3">
            <input
              className="form-control"
              placeholder="Nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          {!editingId && (
            <>
              <div className="col-md-3">
                <input
                  className="form-control"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <input
                  type="password"
                  className="form-control"
                  placeholder="Senha"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="col-md-2">
            <select
              className="form-select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="INSTRUCTOR">Instrutor</option>
              <option value="COORDINATION">Coordenacao</option>
            </select>
          </div>

          <div className="col-md-1">
            <button className="btn btn-primary w-100" onClick={handleSubmit}>
              {editingId ? "Salvar" : "Criar"}
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm p-4">
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">Todos os perfis</option>
              <option value="INSTRUCTOR">Instrutor</option>
              <option value="COORDINATION">Coordenacao</option>
            </select>
          </div>

          <div className="col-md-5">
            <input
              className="form-control"
              placeholder="Buscar por nome"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
            />
          </div>

          <div className="col-md-4 d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={applyFilters}>
              Filtrar
            </button>
            <button className="btn btn-outline-secondary" onClick={clearFilters}>
              Limpar
            </button>
          </div>
        </div>

        <table className="table table-hover">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Ultimo acesso</th>
              <th>Total de acessos</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {instructors.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted">
                  Nenhum usuario encontrado para os filtros aplicados.
                </td>
              </tr>
            ) : (
              instructors.map((inst) => (
                <tr key={inst.id}>
                  <td>{inst.name}</td>
                  <td>{inst.email}</td>
                  <td>{getRoleLabel(inst.role)}</td>
                  <td>{formatDateTime(inst.lastAccessAt)}</td>
                  <td>{inst.accessCount || 0}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(inst)}>
                      Editar
                    </button>

                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => fetchAccessLogs(inst)}
                    >
                      Historico
                    </button>

                    <button
                      className="btn btn-sm btn-outline-warning me-2"
                      onClick={() => handleResetPassword(inst.id)}
                    >
                      Resetar Senha
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(inst.id)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="d-flex justify-content-center align-items-center mt-3">
          <button
            className="btn btn-outline-secondary me-3"
            disabled={page <= 1}
            onClick={() => fetchData(page - 1, roleFilter, query)}
          >
            Anterior
          </button>

          <span>
            Pagina {page} de {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary ms-3"
            disabled={page >= totalPages}
            onClick={() => fetchData(page + 1, roleFilter, query)}
          >
            Proxima
          </button>
        </div>
      </div>

      {accessLogsInstructor && (
        <div className="custom-modal-overlay" onClick={closeAccessLogs}>
          <div className="custom-modal access-log-modal" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-1">Historico de acessos</h4>
                <div className="text-muted">
                  {accessLogsInstructor.name} ({accessLogsInstructor.email})
                </div>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={closeAccessLogs}>
                Fechar
              </button>
            </div>

            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Data e hora</th>
                </tr>
              </thead>
              <tbody>
                {accessLogs.length === 0 ? (
                  <tr>
                    <td className="text-muted">
                      Nenhum acesso registrado para este usuario.
                    </td>
                  </tr>
                ) : (
                  accessLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatDateTime(log.loggedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-center align-items-center mt-3">
              <button
                className="btn btn-outline-secondary me-3"
                disabled={accessLogsPage <= 1}
                onClick={() => fetchAccessLogs(accessLogsInstructor, accessLogsPage - 1)}
              >
                Anterior
              </button>

              <span>
                Pagina {accessLogsPage} de {accessLogsTotalPages}
              </span>

              <button
                className="btn btn-outline-secondary ms-3"
                disabled={accessLogsPage >= accessLogsTotalPages}
                onClick={() => fetchAccessLogs(accessLogsInstructor, accessLogsPage + 1)}
              >
                Proxima
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
